import Text from '@deprecated-ui/common/Text';
import If from 'global/ErrorHandlers/If';
import SEO from 'global/SEO';
import Link from 'next/link';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import HtmlParser from 'html-react-parser';
import sanitizeHtml from 'sanitize-html';

import useIsUserSyariah from 'global/TradingModal/hooks/useIsUserSyariah';
import {
  setValue,
  useOrderbookStore as wsStore,
} from 'global/MainLayout/SideWidget/Watchlist/Orderbook/store';
import { useLivePrice } from 'global/Datafeed/UseLivePrice/useLivePrice';
import { LivePriceSymbolsKeyEnum } from 'global/Datafeed/UseLivePrice/liveprice.store.types';
import { selectors as credentialsSelectors } from 'lib/entities/credentials';
import { clevertapDayTradeStockListViewed } from 'lib/analytics/securities';

import useBondsAnalytics from 'global/TradingModal/RealTradingModal/ModalBonds/useBondsAnalytics';
import { Flex } from '@deprecated-ui/common';
import Image from 'global/Image';
import TableCatalog from './TableCatalog';
import {
  Chip,
  ContentContainer,
  ContentWrapper,
  UpdateBannerContainer,
  HeaderTitle,
  HeaderWrapper,
  LoadingSpinner,
} from './styled';
import { effects, selectors } from './slice';
import { convertToSlug } from '../../utils/general';
import { SPECIAL_BOARD_ALIAS, SPECIAL_SUBSECTOR_ALIAS } from './constants';
import EmptyTable from './EmptyTable';
import PopoverInfo from './PopoverInfo';

const FR_BONDS_SECTOR_ALIAS = 'fr-bonds';

interface CatalogProps {
  sector: any;
  subsector: string;
}

function Catalog({ sector, subsector }: CatalogProps) {
  const dispatch = useDispatch();
  const [symbolList, setSymbolList] = useState([]);
  const { subsectors, sectors, pageTitle, isLoading, companies } =
    useSelector(selectors);
  const [companyList, setCompanyList] = useState([]);
  const { isLoggedIn } = useSelector(credentialsSelectors);
  const { FRBondsNavigateTrackers } = useBondsAnalytics();
  const isUserSyariah = useIsUserSyariah();

  const livePrice = wsStore(({ liveprice }) => liveprice);

  const getCatalogCompanies = async () => {
    await dispatch(
      effects.getCompanyList({ sector, subsector, isCatalog: true }),
    );
  };

  const livePriceOnMessage = useCallback((message) => {
    setValue({
      key: 'liveprice',
      symbol: message.stockCode,
      type: 'update_general_pricedata',
      value: { ...livePrice, ...message },
    });
  }, []);

  useLivePrice(
    symbolList,
    LivePriceSymbolsKeyEnum.CATALOG,
    'catalog',
    livePriceOnMessage,
  );

  const isDayTrade = subsector?.toLocaleLowerCase() === 'daytrade';

  const isShowDayTradeMultipler = useMemo(
    () => isDayTrade && !isUserSyariah,
    [isDayTrade, isUserSyariah],
  );

  const showSubsectorList = useMemo(() => {
    if (
      subsectors.length > 0 &&
      subsectors[0].alias1 === convertToSlug(sector)
    ) {
      return false;
    }
    if (subsectors.length === 0) {
      return false;
    }
    if (subsector) {
      return false;
    }
    return true;
  }, [subsectors, subsector]);

  const handleTitle = useMemo(() => {
    if (subsector) {
      const subSectorFound = subsectors?.find(
        (item) => convertToSlug(item.alias1) === convertToSlug(subsector),
      );
      return subSectorFound?.name || subsector;
    }

    if (sector) {
      const sectorFound = sectors?.find(
        (item) => convertToSlug(item.alias1) === convertToSlug(sector),
      );
      return sectorFound?.name || sector;
    }

    return '';
  }, [subsector, subsectors, sector, sectors]);

  const catalogPageTitle = pageTitle ? `Sektor ${pageTitle}` : undefined;

  const isFRCatalog = convertToSlug(sector) === FR_BONDS_SECTOR_ALIAS;

  useEffect(() => {
    if (sector && isLoggedIn) {
      getCatalogCompanies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, sector, subsector]);

  // Set Company & Symbol List
  useEffect(() => {
    if (companies !== undefined) {
      if (sector && subsector) {
        setCompanyList(companies);
        const listSymbol = companies.map((company) => company.symbol);
        setSymbolList(listSymbol);
      }

      if (sector && !subsector) {
        setCompanyList(companies);
        const listSymbol = companies.map((company) => company.symbol);
        setSymbolList(listSymbol);
      }
    }
    return () => {
      setCompanyList([]);
    };
  }, [sector, subsector, companies]);

  useEffect(() => {
    if (isDayTrade) {
      clevertapDayTradeStockListViewed({
        sourcePage: 'Company Page',
      });
    }
  }, [isDayTrade]);

  useEffect(() => {
    if (isFRCatalog) {
      FRBondsNavigateTrackers.onBondPageViewed('Catalog Page');
    }
  }, [isFRCatalog]);

  const showBanner = useMemo(() => {
    if (
      (subsector?.toUpperCase() === SPECIAL_SUBSECTOR_ALIAS.FCA_OUT ||
        subsector?.toUpperCase() === SPECIAL_SUBSECTOR_ALIAS.UNSUSPENDED) &&
      companyList?.length > 0
    ) {
      return true;
    }

    return false;
  }, [subsector, companyList.length]);

  const showEmptyTable = useMemo(() => {
    if (
      companyList.length === 0 &&
      SPECIAL_BOARD_ALIAS.includes(subsector?.toUpperCase()) &&
      !isLoading
    ) {
      return true;
    }
    return false;
  }, [companyList, subsector, isLoading]);

  const isTradingLimit =
    subsector?.toUpperCase() === SPECIAL_SUBSECTOR_ALIAS.TRADING_LIMIT;

  const emptyTableText = useMemo(() => {
    const slugSubsector = subsector?.toUpperCase();
    if (slugSubsector === SPECIAL_SUBSECTOR_ALIAS.FCA_OUT) {
      return 'Tidak ada saham keluar dari FCA hari ini';
    }
    if (slugSubsector === SPECIAL_SUBSECTOR_ALIAS.UNSUSPENDED) {
      return 'Tidak ada saham Unsuspend hari ini';
    }
    if (slugSubsector === SPECIAL_SUBSECTOR_ALIAS.UMA) {
      return 'Tidak ada saham UMA hari ini';
    }
    return '';
  }, [subsector]);

  if (!sector) {
    return null;
  }

  return (
    <ContentWrapper>
      <SEO title={catalogPageTitle} />
      <ContentContainer>
        <HeaderWrapper>
          {isLoading && <LoadingSpinner id="catalog-loading-spinner" />}
          <If condition={!isLoading}>
            <Flex justify="space-between" align="center">
              <div>
                <Flex align="center">
                  <HeaderTitle id="catalog-title">{handleTitle}</HeaderTitle>
                  {isTradingLimit && <PopoverInfo />}
                </Flex>
                <Text id="catalog-stock-count">
                  {companyList.length || '0'} Stocks
                </Text>
              </div>
              {showBanner && (
                <UpdateBannerContainer>
                  <Image
                    src="/icon/info-circle-grey.svg"
                    style={{ marginRight: 6 }}
                  />
                  <div>
                    {HtmlParser(sanitizeHtml(companyList[0]?.label_text))}
                  </div>
                </UpdateBannerContainer>
              )}
            </Flex>

            {showSubsectorList &&
              subsectors?.map((item) => (
                <Link
                  href={`/catalog/${sector}/${item.alias1}`}
                  passHref
                  key={item.alias1}
                >
                  <Chip className="catalog-subsector-item">{item.name}</Chip>
                </Link>
              ))}
          </If>
        </HeaderWrapper>
        {showEmptyTable ? (
          <EmptyTable text={emptyTableText} />
        ) : (
          <TableCatalog
            companyList={companyList}
            isLoading={isLoading}
            liveData={livePrice}
            showMultiplier={isShowDayTradeMultipler}
            isFRCatalog={isFRCatalog}
            subsectorAlias={convertToSlug(subsector)}
            tableTitle={handleTitle}
          />
        )}
      </ContentContainer>
    </ContentWrapper>
  );
}

export default Catalog;
